"use client";
import { useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import Navbar from "@/components/Navbar/Navbar";
import SidebarRight from "@/components/Navbar/SidebarRight";
import { installChromeColorNavigationPatch } from "@/app/helpers/usePageChromeColor";
import { getDealerAuthHeaders } from "@/app/helpers/getDealerAuthHeaders";
import { useAuth } from "@/context/AuthProvider";
import { useDealerWebActivity } from "@/context/DealerWebActivityProvider";
import useBackendUrl from "@/hooks/useBackendUrl";
import { canDealerSell } from "@/app/helpers/dealerAccess";
import { useDealerVehicleMarket } from "@/context/DealerVehicleMarketProvider";
import DealerFeeBanner from "@/app/marktplatz/sections/DealerFeeBanner/DealerFeeBanner";

const hasOpenBelowReserveOffer = (vehicle, now = Date.now()) => {
  const entries = Array.isArray(vehicle?.counterOffer?.entries)
    ? vehicle.counterOffer.entries
    : [];
  const finalPrice = Number(vehicle?.auction?.finalPrice);
  const dealerReserve = Number(
    vehicle?.dealerPricing?.targetPrice || vehicle?.minPrice,
  );
  const sellStatus = String(vehicle?.auction?.sellStatus || "")
    .trim()
    .toLowerCase();
  const counterOfferStatus = String(vehicle?.counterOffer?.status || "")
    .trim()
    .toLowerCase();
  const offerValidUntil = Date.parse(vehicle?.auction?.offerValidUntil || "");

  return (
    entries.length > 0 &&
    Number.isFinite(finalPrice) &&
    finalPrice > 0 &&
    Number.isFinite(dealerReserve) &&
    dealerReserve > finalPrice &&
    sellStatus === "waiting-for-seller" &&
    (!counterOfferStatus || counterOfferStatus === "ongoing") &&
    (!Number.isFinite(offerValidUntil) || offerValidUntil > now)
  );
};

const hasPendingHighestBidReview = (vehicle, now = Date.now()) => {
  const entries = Array.isArray(vehicle?.counterOffer?.entries)
    ? vehicle.counterOffer.entries
    : [];
  const finalPrice = Number(vehicle?.auction?.finalPrice);
  const sellStatus = String(vehicle?.auction?.sellStatus || "")
    .trim()
    .toLowerCase();
  const counterOfferStatus = String(vehicle?.counterOffer?.status || "")
    .trim()
    .toLowerCase();
  const offerValidUntil = Date.parse(vehicle?.auction?.offerValidUntil || "");
  const auctionEndedAt = Date.parse(vehicle?.auction?.endTime || "");
  const fallbackDeadline = Number.isFinite(auctionEndedAt)
    ? auctionEndedAt + 48 * 60 * 60 * 1000
    : NaN;
  const deadline = Number.isFinite(offerValidUntil)
    ? Number.isFinite(fallbackDeadline)
      ? Math.min(offerValidUntil, fallbackDeadline)
      : offerValidUntil
    : fallbackDeadline;

  return (
    entries.length === 0 &&
    Number.isFinite(finalPrice) &&
    finalPrice > 0 &&
    sellStatus === "waiting-for-seller" &&
    !["rejected", "declined", "expired"].includes(counterOfferStatus) &&
    (!Number.isFinite(deadline) || deadline > now)
  );
};

export default function NavbarShell({ patternStrings }) {
  const pathname = usePathname();
  const api = useBackendUrl();
  const { authInitialized, userData } = useAuth();
  const { enabled: dealerVehicleMarketEnabled } = useDealerVehicleMarket();
  const { tasks: salesTasks, hasOverviewTask } = useDealerWebActivity();
  const hasSellerAccess =
    dealerVehicleMarketEnabled && canDealerSell(userData);
  const [isOpen, setIsOpen] = useState(false);
  const [hasVehicleListAction, setHasVehicleListAction] = useState(false);
  const [hasOpenCounterOffer, setHasOpenCounterOffer] = useState(false);

  // RegExp erst hier erzeugen (nur 1×, dank useMemo)
  const patterns = useMemo(
    () => ({
      withDark: patternStrings.withDark.map((s) => new RegExp(s, "i")),
      nonSticky: patternStrings.nonSticky.map((s) => new RegExp(s, "i")),
      rerouteLanding: patternStrings.rerouteLanding, // bleibt Array<string>
    }),
    [patternStrings]
  );

  const isNavbarDark = patterns.withDark.some((rx) => rx.test(pathname));
  const isNavbarSticky = !patterns.nonSticky.some((rx) => rx.test(pathname));
  const isRerouting = patterns.rerouteLanding.includes(pathname);
  // The activity endpoint is the authoritative, role-aware source for actions
  // that arrived while the user was away. Keep the vehicle-list check below as
  // a fallback for re-auction eligibility and older records.
  const hasCounterOfferTask = salesTasks.some((task) =>
    task?.href?.startsWith("/verkaufen/gegenangebote/"),
  );
  const hasHighestBidTask = salesTasks.some((task) =>
    task?.href?.startsWith("/verkaufen/hoechstgebot?"),
  );
  const hasVehicleAction = hasVehicleListAction || hasHighestBidTask;
  const hasCounterOfferAction = hasOpenCounterOffer || hasCounterOfferTask;

  useEffect(() => {
    installChromeColorNavigationPatch();
  }, []);

  useEffect(() => {
    if (!api || !authInitialized || !hasSellerAccess) {
      setHasVehicleListAction(false);
      setHasOpenCounterOffer(false);
      return undefined;
    }

    const controller = new AbortController();
    let isCurrent = true;

    const loadDealerVehicleStatus = async () => {
      try {
        const authHeaders = await getDealerAuthHeaders();
        if (!authHeaders.Authorization) {
          if (isCurrent) {
            setHasVehicleListAction(false);
            setHasOpenCounterOffer(false);
          }
          return;
        }

        const response = await fetch(`${api}/auction/dealer-vehicles`, {
          cache: "no-store",
          credentials: "include",
          headers: authHeaders,
          signal: controller.signal,
        });
        if (!response.ok) {
          if (isCurrent) {
            setHasVehicleListAction(false);
            setHasOpenCounterOffer(false);
          }
          return;
        }

        const data = await response.json().catch(() => ({}));
        const vehicles = Array.isArray(data?.vehicles) ? data.vehicles : [];
        const hasAction = vehicles.some(
          (vehicle) =>
            vehicle?.reauction?.eligible === true ||
            hasPendingHighestBidReview(vehicle),
        );
        const hasCounterOffer = vehicles.some((vehicle) =>
          hasOpenBelowReserveOffer(vehicle),
        );
        if (isCurrent) {
          setHasVehicleListAction(hasAction);
          setHasOpenCounterOffer(hasCounterOffer);
        }
      } catch (error) {
        if (error?.name !== "AbortError" && isCurrent) {
          setHasVehicleListAction(false);
          setHasOpenCounterOffer(false);
        }
      }
    };

    void loadDealerVehicleStatus();
    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [api, authInitialized, hasSellerAccess, pathname]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1040px)");
    const closeOnDesktop = (event) => {
      if (!event.matches) setIsOpen(false);
    };

    mediaQuery.addEventListener("change", closeOnDesktop);
    return () => mediaQuery.removeEventListener("change", closeOnDesktop);
  }, []);

  return (
    <>
      <SidebarRight
        hasVehicleAction={hasVehicleAction}
        hasOpenCounterOffer={hasCounterOfferAction}
        hasOverviewTask={hasOverviewTask}
        isSidebarOpen={isOpen}
        setIsSidebarOpen={setIsOpen}
      />
      <div
        className={
          isOpen
            ? "overlay Navbar-mobile-overlay active"
            : "overlay Navbar-mobile-overlay"
        }
        onClick={() => setIsOpen(false)}
      />
      {pathname === "/marktplatz" && <DealerFeeBanner />}
      <Navbar
        hasVehicleAction={hasVehicleAction}
        hasOpenCounterOffer={hasCounterOfferAction}
        hasOverviewTask={hasOverviewTask}
        isNavbarDark={isNavbarDark}
        isNavbarSticky={isNavbarSticky}
        isReroutingToLanding={isRerouting}
        setIsSidebarOpen={setIsOpen}
      />
    </>
  );
}
